/**
 * Company Portal events API helpers (PROLINE-2274 payloads & responses).
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import store from "../../store";
import messages from "../../assets/locale/messages";
import { directories, uploadToS3 } from "../S3";
import { eventStatuses, SA_UTC_OFFSET_MINUTES } from "../Constants";

dayjs.extend(utc);

export const COMPANY_PORTAL_FORM_VARIANT = "company-portal";

export const getLocalizedPersonName = (person, lang = "en") => {
  if (!person || typeof person !== "object") return "";

  const en = String(
    person.full_name_en ||
      [
        person.first_name_en ?? person.first_name,
        person.middle_name_en ?? person.middle_name,
        person.last_name_en ?? person.last_name,
      ]
        .filter(Boolean)
        .join(" "),
  ).trim();

  const ar = String(
    person.full_name_ar ||
      [person.first_name_ar, person.middle_name_ar, person.last_name_ar]
        .filter(Boolean)
        .join(" "),
  ).trim();

  return (
    (lang === "ar" ? ar || en : en || ar) ||
    String(person.name || "").trim()
  );
};

/**
 * Company portal: finished or cancelled events are view-only (no Edit action / edit route).
 * Accepts API variants e.g. `cancelled` vs `canceled`, `completed` if ever used.
 */
export const isCompanyPortalEventReadOnly = (status) => {
  if (status == null || status === "") return false;
  const s = String(status).trim().toLowerCase();
  if (s === "cancelled" || s === "canceled") return true;
  if (s === String(eventStatuses.past)) return true;
  if (s === "completed" || s === "complete") return true;
  return false;
};

const SHIFT_TIME_HM = /^(\d{1,2}):(\d{2})$/;
const EXPLICIT_TIMEZONE_RE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Datetime string suitable for Rails. Old Admin DateTimePicker sent values with
 * Saudi offset (+03:00), so preserve selected wall-clock times in that offset.
 * Pass `asNullWhenEmpty` so empty/invalid -> `null` (serializes in JSON).
 */
export const toPortalDateTime = (value, asNullWhenEmpty = false) => {
  if (value == null || value === "") return asNullWhenEmpty ? null : undefined;
  const parsed = dayjs(value);
  if (!parsed.isValid()) return asNullWhenEmpty ? null : undefined;
  const hasExplicitTimezone =
    typeof value === "string" && EXPLICIT_TIMEZONE_RE.test(value.trim());
  return parsed
    .utcOffset(SA_UTC_OFFSET_MINUTES, !hasExplicitTimezone)
    .format();
};

/**
 * `UITimeField` persists shift times as `HH:mm`. Rails needs full datetimes.
 * Merge onto the event's start calendar day (same as Admin step context).
 */
export const shiftClockToIsoForPayload = (value, eventStartAt) => {
  if (value == null || value === "") return undefined;
  const full = toPortalDateTime(value);
  if (full) return full;
  const trimmed = String(value).trim();
  const match = SHIFT_TIME_HM.exec(trimmed);
  if (!match || !eventStartAt) return undefined;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return undefined;
  const base = dayjs(eventStartAt);
  if (!base.isValid()) return undefined;
  return base
    .utcOffset(SA_UTC_OFFSET_MINUTES)
    .hour(h)
    .minute(m)
    .second(0)
    .millisecond(0)
    .format();
};

const adjustOvernightShiftEnd = (startIso, endIso) => {
  if (!startIso || !endIso) return endIso;
  if (dayjs(endIso).isBefore(dayjs(startIso))) {
    return dayjs(endIso)
      .add(1, "day")
      .utcOffset(SA_UTC_OFFSET_MINUTES)
      .format();
  }
  return endIso;
};

/** First human-readable API error message from Axios error envelope. */
export const getPortalApiErrorMessage = (error, fallback) => {
  // No response: the request never reached the backend. Same copy as the interceptor.
  if (error?.message === "Network Error" && !error?.response) {
    const lang = store.getState()?.locale?.lang;
    return (messages[lang] ?? messages.en).shared.noInternetConnection;
  }
  const errs = error?.response?.data?.errors;
  if (errs && typeof errs === "object") {
    for (const v of Object.values(errs)) {
      if (Array.isArray(v) && v.length) return String(v[0]);
      if (typeof v === "string" && v.trim()) return v;
    }
  }
  const msg =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message;
  return (typeof msg === "string" && msg.trim()) ? msg : fallback;
};

const JSON_API_RESOURCE_META = new Set([
  "type",
  "id",
  "attributes",
  "relationships",
  "links",
  "meta",
]);

const normalizeJsonApiType = (t) => String(t || "").replace(/-/g, "_");

const matchesJsonApiRef = (item, ref) =>
  Boolean(
    item &&
      ref &&
      normalizeJsonApiType(item.type) === normalizeJsonApiType(ref.type) &&
      String(item.id) === String(ref.id),
  );

/**
 * Company Portal may return JSON:API with `event_roles` only in `included`.
 * Without hydrated roles, the form never gets DB ids — the next PATCH resends
 * bare `role_id` rows and Rails returns "You already entered this role".
 *
 * If `attributes` already contain `event_roles` but without nested shifts
 * (or fewer shifts than in `included`), we still hydrate from `included` so
 * consumers like the applicants shift filter see every `event_role_shift`.
 */
const augmentAttrsWithJsonApiIncluded = (attrs, node, payload) => {
  const included = payload?.included;
  if (!Array.isArray(included) || included.length === 0) return attrs;

  const relData =
    node.relationships?.event_roles?.data ??
    node.relationships?.eventRoles?.data;
  if (!relData) return attrs;

  const findIncluded = (ref) =>
    included.find((item) => matchesJsonApiRef(item, ref));

  const hydrateTrainingFromShift = (shiftResource) => {
    const tRef =
      shiftResource?.relationships?.training?.data ??
      shiftResource?.relationships?.training_attributes?.data;
    if (!tRef) return undefined;
    const ref = Array.isArray(tRef) ? tRef[0] : tRef;
    const tRes = findIncluded(ref);
    if (!tRes) return undefined;
    return {
      id: tRes.id,
      ...(tRes.attributes && typeof tRes.attributes === "object"
        ? tRes.attributes
        : {}),
    };
  };

  const hydrateShift = (shiftResource) => {
    if (!shiftResource) return null;
    const row = {
      id: shiftResource.id,
      ...(shiftResource.attributes && typeof shiftResource.attributes === "object"
        ? shiftResource.attributes
        : {}),
    };
    const training = hydrateTrainingFromShift(shiftResource);
    if (training) row.training_attributes = training;
    return row;
  };

  const hydrateRole = (roleResource) => {
    if (!roleResource) return null;
    const row = {
      id: roleResource.id,
      ...(roleResource.attributes && typeof roleResource.attributes === "object"
        ? roleResource.attributes
        : {}),
    };
    const shiftRel =
      roleResource.relationships?.event_role_shifts?.data ??
      roleResource.relationships?.eventRoleShifts?.data;
    if (!shiftRel) return row;
    const shiftRefs = Array.isArray(shiftRel) ? shiftRel : [shiftRel];
    const shifts = shiftRefs
      .map(findIncluded)
      .filter(Boolean)
      .map(hydrateShift);
    if (shifts.length) {
      row.event_role_shifts = shifts;
      row.event_role_shifts_attributes = shifts;
    }
    return row;
  };

  const refs = Array.isArray(relData) ? relData : [relData];
  const rolesFromIncluded = refs
    .map(findIncluded)
    .filter(Boolean)
    .map(hydrateRole)
    .filter(Boolean);

  if (!rolesFromIncluded.length) return attrs;

  const roleShiftCount = (role) => {
    if (!role || typeof role !== "object") return 0;
    const a = role.event_role_shifts;
    const b = role.event_role_shifts_attributes;
    const n = (x) => (Array.isArray(x) ? x.length : 0);
    return Math.max(n(a), n(b));
  };

  const mergeRoleShiftsFromIncluded = (attrRole, incRole) => {
    if (!incRole) return attrRole;
    const incShifts =
      incRole.event_role_shifts ?? incRole.event_role_shifts_attributes;
    if (!Array.isArray(incShifts) || !incShifts.length) return attrRole;
    if (roleShiftCount(attrRole) >= incShifts.length) return attrRole;
    return {
      ...attrRole,
      event_role_shifts: incShifts,
      event_role_shifts_attributes: incShifts,
    };
  };

  const rolesFromAttrs =
    attrs.event_roles ?? attrs.event_roles_attributes ?? [];

  if (!Array.isArray(rolesFromAttrs) || rolesFromAttrs.length === 0) {
    return {
      ...attrs,
      event_roles: rolesFromIncluded,
      event_roles_attributes: rolesFromIncluded,
    };
  }

  const includedById = new Map(
    rolesFromIncluded.map((r) => [String(r.id), r]),
  );

  const merged = rolesFromAttrs.map((role) => {
    const rid = role?.id ?? role?.attributes?.id;
    if (rid == null) return role;
    const inc = includedById.get(String(rid));
    return mergeRoleShiftsFromIncluded(role, inc);
  });

  return {
    ...attrs,
    event_roles: merged,
    event_roles_attributes: merged,
  };
};

const zonePersistedId = (z) =>
  z == null ? undefined : z.id ?? z?.attributes?.id;

const zoneRowMissingPersistedId = (z) => {
  const id = zonePersistedId(z);
  return (
    id === undefined ||
    id === null ||
    (typeof id === "string" && id.trim() === "")
  );
};

/**
 * Attach JSON:API zone `id`s to attribute rows when the serializer omits IDs on
 * `zones_attributes` but lists full resources under `included` (otherwise PATCH
 * re-sends unnamed rows → "You already entered this zone").
 */
const mergePortalZoneRowsWithIncluded = (existingRows, includedZonesFlat) => {
  if (
    !Array.isArray(existingRows) ||
    !Array.isArray(includedZonesFlat) ||
    !includedZonesFlat.length
  ) {
    return existingRows;
  }

  const usedInc = new Set();
  const nameKey = (z) =>
    String(z?.name ?? z?.attributes?.name ?? "")
      .trim()
      .toLowerCase();

  const takeIncludedMatch = (row, rowIdx) => {
    const rid = zonePersistedId(row);
    if (rid != null && `${rid}` !== "") {
      const i = includedZonesFlat.findIndex(
        (inc, idx) =>
          !usedInc.has(idx) &&
          zonePersistedId(inc) != null &&
          `${zonePersistedId(inc)}` === `${rid}`,
      );
      if (i >= 0) {
        usedInc.add(i);
        return includedZonesFlat[i];
      }
    }

    const nk = nameKey(row);
    if (nk) {
      const i = includedZonesFlat.findIndex(
        (inc, idx) => !usedInc.has(idx) && nameKey(inc) === nk,
      );
      if (i >= 0) {
        usedInc.add(i);
        return includedZonesFlat[i];
      }
    }

    if (
      typeof rowIdx === "number" &&
      includedZonesFlat[rowIdx] &&
      !usedInc.has(rowIdx)
    ) {
      usedInc.add(rowIdx);
      return includedZonesFlat[rowIdx];
    }

    const fallback = includedZonesFlat.findIndex((_, idx) => !usedInc.has(idx));
    if (fallback >= 0) {
      usedInc.add(fallback);
      return includedZonesFlat[fallback];
    }
    return null;
  };

  return existingRows.map((row, idx) => {
    if (!zoneRowMissingPersistedId(row)) return row;
    const inc = takeIncludedMatch(row, idx);
    if (!inc) return row;
    const pid = zonePersistedId(inc);
    return { ...inc, ...row, id: pid };
  });
};

/**
 * Same pattern as event_roles — zones nested in JSON:API `included` must land on
 * `attrs.zones_attributes` or the next PATCH re-creates duplicates ("already entered").
 */
const augmentAttrsWithZonesIncluded = (attrs, node, payload) => {
  const existingRaw =
    attrs.zones_attributes ?? attrs.zones ?? [];
  const existing = Array.isArray(existingRaw) ? existingRaw : [];

  const included = payload?.included;
  if (!Array.isArray(included) || included.length === 0) return attrs;

  const relData =
    node.relationships?.zones?.data ??
    node.relationships?.event_zones?.data ??
    node.relationships?.zones_attributes?.data;

  if (!relData) return attrs;

  const findIncluded = (ref) =>
    included.find((item) => matchesJsonApiRef(item, ref));

  const hydrateZoneSupervisor = (resource) => {
    const rel =
      resource?.relationships?.supervisor?.data ??
      resource?.relationships?.zone_supervisor?.data ??
      resource?.relationships?.user?.data;
    if (!rel) return undefined;

    const ref = Array.isArray(rel) ? rel[0] : rel;
    const supervisorResource = findIncluded(ref);
    if (!supervisorResource) return undefined;

    return {
      id: supervisorResource.id,
      ...(supervisorResource.attributes &&
      typeof supervisorResource.attributes === "object"
        ? supervisorResource.attributes
        : {}),
    };
  };

  const hydrateZone = (resource) => {
    if (!resource) return null;
    const zone = {
      id: resource.id,
      ...(resource.attributes && typeof resource.attributes === "object"
        ? resource.attributes
        : {}),
    };
    const supervisor = hydrateZoneSupervisor(resource);
    if (supervisor) {
      zone.supervisor = supervisor;
      zone.supervisor_id = zone.supervisor_id ?? supervisor.id;
    }
    return zone;
  };

  const refs = Array.isArray(relData) ? relData : [relData];
  const fromIncluded = refs
    .map(findIncluded)
    .filter(Boolean)
    .map(hydrateZone)
    .filter(Boolean);

  if (!fromIncluded.length) return attrs;

  const lacksPersistedZoneIds =
    existing.length === 0 || existing.some(zoneRowMissingPersistedId);

  let nextZones;
  if (existing.length === 0) {
    nextZones = fromIncluded;
  } else if (lacksPersistedZoneIds) {
    nextZones = mergePortalZoneRowsWithIncluded(existing, fromIncluded);
  } else {
    return attrs;
  }

  return { ...attrs, zones_attributes: nextZones };
};

export const unwrapPortalEventEnvelope = (response) => {
  const payload = response?.data;
  const node = payload?.data;
  if (!node || typeof node !== "object") return null;

  let attrs =
    node.attributes && typeof node.attributes === "object"
      ? { ...node.attributes }
      : {};

  Object.keys(node).forEach((key) => {
    if (JSON_API_RESOURCE_META.has(key)) return;
    if (attrs[key] === undefined && node[key] != null) attrs[key] = node[key];
  });

  attrs = augmentAttrsWithJsonApiIncluded(attrs, node, payload);
  attrs = augmentAttrsWithZonesIncluded(attrs, node, payload);

  const numericId = attrs.id ?? node.id;
  const id = numericId != null ? String(numericId) : null;
  if (id != null && attrs.id == null) {
    attrs = { ...attrs, id: numericId };
  }

  return { id, attributes: attrs, raw: node };
};

const readAttr = (node, key) => node?.[key] ?? node?.attributes?.[key];

const readRoleId = (role) =>
  readAttr(role, "role_id") ?? role?.role?.id ?? role?.attributes?.role?.id;

const readRoleName = (role) =>
  readAttr(role, "role_name") ??
  role?.role?.name ??
  role?.attributes?.role?.name ??
  "";

const readShifts = (role) =>
  readAttr(role, "event_role_shifts") ??
  readAttr(role, "event_role_shifts_attributes") ??
  [];

const readTraining = (shift) =>
  readAttr(shift, "training") ?? readAttr(shift, "training_attributes");

/** Localized single-line label for a raw `event_types` row from GET `/lookups`. */
export const getEventTypeLookupOptionLabel = (row, lang) => {
  if (!row || typeof row !== "object") return "";
  const isAr = lang === "ar";
  const name = String(row.name ?? "").trim();
  const name_en = String(
    row.name_en ?? row.name ?? row.label ?? "",
  ).trim();
  const name_ar = String(row.name_ar ?? "").trim();
  return (
    String(
      isAr
        ? row.name_ar ?? row.name ?? row.label
        : row.name_en ?? row.name ?? row.label ?? "",
    ).trim() ||
    name ||
    name_en ||
    name_ar ||
    String(row.id ?? "")
  );
};

/**
 * `event_type` (object/string) takes precedence over `event_type_id` when both exist (API shapes vary).
 */
const readEventTypeIdForForm = (attrs = {}) => {
  const raw = attrs?.event_type;
  if (raw != null && raw !== "") {
    if (typeof raw === "object") {
      const id = raw.id ?? raw.key ?? raw.slug ?? raw.value;
      if (id == null || id === "") return null;
      const idNum = Number(id);
      return Number.isFinite(idNum) ? idNum : id;
    }
    const idNum = Number(raw);
    if (Number.isFinite(idNum)) return idNum;
    const s = String(raw).trim();
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }
  const fk = attrs?.event_type_id;
  if (fk == null || fk === "") return null;
  const idNum = Number(fk);
  return Number.isFinite(idNum) ? idNum : fk;
};

/** Drop nested `event_type`; keep or resolve `event_type_id` for Rails. */
export const mergePortalEventTypeIntoPayload = (payload, eventAttrs) => {
  if (!payload || typeof payload !== "object") return payload;
  const rest = { ...payload };
  delete rest.event_type;
  const fromBody = rest.event_type_id;
  const rawId =
    fromBody != null && fromBody !== ""
      ? fromBody
      : readEventTypeIdForForm(eventAttrs ?? {});
  if (rawId == null || rawId === "") return rest;
  const idNum = Number(rawId);
  return {
    ...rest,
    event_type_id: Number.isFinite(idNum) ? idNum : rawId,
  };
};

export const getProjectManagerInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
};

/** Picker row from admin/company `company_users` list item. */
export const mapCompanyUserToPmOption = (user = {}) => {
  const name = String(user.name || "").trim();
  return {
    id: user.id,
    company_user_id: user.id,
    name,
    email: user.email || "",
    phone: user.phone_number || "",
    profile_picture_url: user.profile_picture_url || null,
    initials: getProjectManagerInitials(name),
  };
};

const readCompanyUserFromEventAssignment = (row = {}) => {
  if (row.company_user && typeof row.company_user === "object") {
    return row.company_user;
  }
  if (row.admin && typeof row.admin === "object") {
    return row.admin;
  }
  return null;
};

/** Form row from GET event `company_user_events` (or legacy `project_managers`). */
export const mapCompanyUserEventToFormPm = (row = {}) => {
  const companyUser = readCompanyUserFromEventAssignment(row);
  const companyUserId = companyUser?.id ?? row.company_user_id ?? row.admin_id;
  if (companyUserId == null || companyUserId === "") return null;

  const name = String(companyUser?.name || row.name || "").trim();
  const responsibility = String(row.responsibility || "").trim();

  return {
    assignmentId: row.id ?? null,
    company_user_id: companyUserId,
    name,
    email: companyUser?.email || "",
    phone: companyUser?.phone_number || "",
    profile_picture_url: companyUser?.profile_picture_url || null,
    initials: getProjectManagerInitials(name),
    responsibility,
    role: responsibility,
  };
};

export const mapEventProjectManagersToFormValues = (attrs = {}) => {
  const fromJoin = attrs.company_user_events;
  if (Array.isArray(fromJoin) && fromJoin.length) {
    return fromJoin.map(mapCompanyUserEventToFormPm).filter(Boolean);
  }

  const legacy = attrs.project_managers;
  if (!Array.isArray(legacy) || !legacy.length) return [];

  return legacy.map(mapCompanyUserEventToFormPm).filter(Boolean);
};

/** Whether the given company user is assigned as a project manager on the event. */
export const isEventProjectManager = (event, companyUserId) => {
  if (companyUserId == null || companyUserId === "") return false;
  return mapEventProjectManagersToFormValues(event ?? {}).some(
    (pm) => String(pm.company_user_id) === String(companyUserId),
  );
};

/** Step 1 form → Rails `company_user_events_attributes`. */
export const buildCompanyUserEventsAttributes = (
  projectManagers = [],
  destroyedProjectManagers = [],
) => {
  const active = (Array.isArray(projectManagers) ? projectManagers : [])
    .map((pm) => {
      const companyUserIdRaw = pm?.company_user_id ?? pm?.id;
      const companyUserId = Number(companyUserIdRaw);
      if (!Number.isFinite(companyUserId)) return null;

      const row = {
        company_user_id: companyUserId,
      };
      const responsibility = String(
        pm?.responsibility ?? pm?.role ?? "",
      ).trim();
      if (responsibility) row.responsibility = responsibility;

      const assignmentId = pm?.assignmentId ?? pm?.joinId ?? pm?.assignment_id;
      if (assignmentId != null && assignmentId !== "") {
        row.id = assignmentId;
      }
      return row;
    })
    .filter(Boolean);

  const destroyed = (Array.isArray(destroyedProjectManagers)
    ? destroyedProjectManagers
    : []
  )
    .map((row) => {
      const id = row?.id ?? row?.assignmentId;
      if (id == null || id === "") return null;
      // Skip destroy when the same join row was re-added (undo remove).
      if (active.some((pm) => String(pm.id) === String(id))) return null;
      return { id, _destroy: true };
    })
    .filter(Boolean);

  if (!active.length && !destroyed.length) return undefined;
  return [...active, ...destroyed];
};

export const mapEventClientToFormValues = (attrs = {}) => {
  const companyClient = attrs.company_client;
  if (companyClient && typeof companyClient === "object") {
    return {
      id: companyClient.id ?? null,
      admin_id: companyClient.client_id ?? null,
    };
  }

  if (attrs.client_id != null && attrs.client_id !== "") {
    return { id: null, admin_id: attrs.client_id };
  }

  if (Array.isArray(attrs.clients) && attrs.clients.length > 0) {
    const row = attrs.clients[0];
    return {
      id: row.id ?? null,
      admin_id: row.client_id ?? row.admin?.id ?? null,
    };
  }

  return { id: null, admin_id: null };
};

/** Dropdown option from a company_client list row (admin or company portal). */
export const mapCompanyClientRowToSelectOption = (row) => ({
  id: row?.client_id ?? row?.id,
  name: row?.name ?? "",
});


export const mapPortalEventToBasicInfoValues = (attrs = {}) => ({
  image_url: attrs.image_url || "",
  name: attrs.name || "",
  event_type_id: readEventTypeIdForForm(attrs),
  address: attrs.address || "",
  description: attrs.description || "",
  payment_period: attrs.payment_period === "daily" ? "daily" : "monthly",
  is_single_day: Boolean(attrs.is_single_day),
  number_of_days: attrs.number_of_days ?? "",
  paid_days_off: attrs.paid_days_off ?? "",
  location: {
    address: attrs.address || "",
    lat: attrs.lat ?? "",
    lng: attrs.lng ?? "",
    radius: attrs.event_radius ?? "",
    city_id: attrs.city?.id ?? null,
  },
  building_start_at: attrs.building_start_at || "",
  building_end_at: attrs.building_end_at || "",
  event_start_at: attrs.event_start_at || "",
  event_end_at: attrs.event_end_at || "",
  dismantling_start_at: attrs.dismantling_start_at || "",
  dismantling_end_at: attrs.dismantling_end_at || "",
  attendance_verification_method:
    attrs.attendance_verification_method || "basic_verification",
  projectManagers: mapEventProjectManagersToFormValues(attrs),
  destroyedProjectManagers: [],
  client: mapEventClientToFormValues(attrs),
});

export const mapPortalEventToRolesValues = (attrs = {}) => ({
  event_roles_attributes: (attrs.event_roles || []).map((role) => ({
    id: readAttr(role, "id"),
    role_id: readRoleId(role) ?? "",
    role_name: readRoleName(role),
  maximum_number_of_males: readAttr(role, "maximum_number_of_males") ?? "",
    maximum_number_of_females:
      readAttr(role, "maximum_number_of_females") ?? "",
    uniform_code: readAttr(role, "uniform_code") || "",
    assigned_applicants_count: (() => {
      const raw =
        readAttr(role, "assigned_applicants_count") ??
        readAttr(role, "filled_assignments_count") ??
        readAttr(role, "current_assignments_count") ??
        readAttr(role, "staff_assigned_count");
      if (raw === "" || raw == null || raw === undefined) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    })(),
    event_role_shifts_attributes: readShifts(role).map((shift) => {
      const training = readTraining(shift);
      return {
        id: readAttr(shift, "id"),
        portal_organizer_checked_in: Boolean(
          readAttr(shift, "organizer_checked_in") ??
            readAttr(shift, "has_organizer_check_in") ??
            readAttr(shift, "organizer_has_checked_in"),
        ),
        shift_type: readAttr(shift, "shift_type") || "",
        starts_at: readAttr(shift, "starts_at") || "",
        ends_at: readAttr(shift, "ends_at") || "",
        payroll_amount: readAttr(shift, "payroll_amount") ?? "",
        payroll_per_day: "",
        before_check_in_margin:
          readAttr(shift, "before_check_in_margin") ?? "",
        after_check_in_margin:
          readAttr(shift, "after_check_in_margin") ?? "",
        before_check_out_margin:
          readAttr(shift, "before_check_out_margin") ?? "",
        after_check_out_margin:
          readAttr(shift, "after_check_out_margin") ?? "",
        training_attributes: training
          ? {
              id: readAttr(training, "id"),
              name: readAttr(training, "name") || "",
              location_address: readAttr(training, "location_address") || "",
              lat: readAttr(training, "lat") ?? null,
              lng: readAttr(training, "lng") ?? null,
              location_radius: readAttr(training, "location_radius") ?? null,
              payroll_amount: readAttr(training, "payroll_amount") ?? "",
              number_of_days: readAttr(training, "number_of_days") ?? "",
              description: readAttr(training, "description") || "",
              starts_at: readAttr(training, "starts_at") || "",
              ends_at: readAttr(training, "ends_at") || "",
              before_check_in_margin:
                readAttr(training, "before_check_in_margin") ?? "",
              after_check_in_margin:
                readAttr(training, "after_check_in_margin") ?? "",
              before_check_out_margin:
                readAttr(training, "before_check_out_margin") ?? "",
              after_check_out_margin:
                readAttr(training, "after_check_out_margin") ?? "",
            }
          : null,
      };
    }),
  })),
});

const emptyPortalZoneFormRow = () => ({
  name: "",
  supervisor_id: "",
  supervisor_name: "",
  applicants_in_zone_count: null,
  image_url: "",
});

const readRequirementId = (row) => {
  const nested = row?.requirement;
  return (
    readAttr(row, "requirement_id") ??
    readAttr(row, "requirementId") ??
    (nested && typeof nested === "object" ? readAttr(nested, "id") : undefined)
  );
};

const portalRequirementDisplayName = (r) => {
  if (!r || typeof r !== "object") return "";
  const nested = r.requirement;
  const fromNested =
    nested && typeof nested === "object"
      ? nested.name ?? readAttr(nested, "name") ?? ""
      : "";
  return String(
    readAttr(r, "name") ??
      readAttr(r, "requirement_name") ??
      fromNested ??
      "",
  ).trim();
};

const readPortalZoneSupervisorId = (z) => {
  const nested =
    z?.supervisor && typeof z.supervisor === "object" ? z.supervisor : null;
  const raw =
    readAttr(z, "supervisor_id") ??
    (nested ? readAttr(nested, "id") : undefined);
  if (raw === "" || raw == null) return "";
  return raw;
};

const portalZoneSupervisorDisplayName = (z, lang = "en") => {
  const nested =
    z?.supervisor && typeof z.supervisor === "object" ? z.supervisor : null;
  if (nested) return getLocalizedPersonName(nested, lang);
  return String(readAttr(z, "supervisor_name") ?? "").trim();
};

const zonesListFromPortalAttrs = (attrs = {}) =>
  attrs.zones_attributes ?? attrs.zones ?? [];

/** Hydrate Zones step from GET / PATCH event attributes (flat or nested). */
export const mapPortalEventToZonesFormValues = (attrs = {}, options = {}) => {
  const lang = options.lang || "en";
  const zs = zonesListFromPortalAttrs(attrs);
  const zones_attributes =
    Array.isArray(zs) && zs.length
      ? zs.map((z) => ({
          ...emptyPortalZoneFormRow(),
          id: readAttr(z, "id"),
          name: readAttr(z, "name") || "",
          applicants_in_zone_count: (() => {
            const raw =
              readAttr(z, "applicants_in_zone_count") ??
              readAttr(z, "applicants_count") ??
              readAttr(z, "current_applicants_count") ??
              readAttr(z, "assigned_applicants_count");
            if (raw === "" || raw == null || raw === undefined) return null;
            const n = Number(raw);
            return Number.isFinite(n) ? n : null;
          })(),
          supervisor_id: readPortalZoneSupervisorId(z),
          supervisor_name:
            portalZoneSupervisorDisplayName(z, lang) ||
            String(readAttr(z, "supervisor_name") ?? "").trim(),
          image_url: readAttr(z, "image_url") || "",
        }))
      : [emptyPortalZoneFormRow()];

  const reqs =
    attrs.event_requirements ??
    attrs.event_requirements_attributes ??
    attrs.logistics_requirements_attributes ??
    attrs.logistics_requirements ??
    attrs.requirements ??
    [];

  const logistics_requirements_attributes =
    Array.isArray(reqs) && reqs.length
      ? reqs.map((r) => {
          const nested =
            r.requirement && typeof r.requirement === "object"
              ? r.requirement
              : null;
          return {
            id: readAttr(r, "id"),
            requirement_id: readRequirementId(r) ?? "",
            name:
              readAttr(r, "name") ??
              readAttr(r, "requirement_name") ??
              (nested ? nested.name ?? readAttr(nested, "name") : "") ??
              "",
            quantity: (() => {
              const q = readAttr(r, "quantity") ?? r?.quantity;
              return q == null ? "" : String(q);
            })(),
            notes:
              readAttr(r, "notes") ??
              readAttr(r, "explanation") ??
              readAttr(r, "description") ??
              "",
          };
        })
      : [];

  return {
    zones_attributes,
    logistics_requirements_attributes,
    zones_image_url: readAttr(attrs, "zones_image_url") || "",
  };
};

/** After PATCH — attach DB zone ids without losing in-progress edits. */
export const mergePersistedPortalZonesIntoFormValues = (zones = [], attrs) => {
  const persisted = zonesListFromPortalAttrs(attrs);
  if (!Array.isArray(persisted) || !persisted.length) return zones;

  const usedIndices = new Set();

  const takePersistedZone = (zone) => {
    let pick = persisted.findIndex(
      (candidate, index) =>
        !usedIndices.has(index) &&
        zone.id != null &&
        String(readAttr(candidate, "id")) === String(zone.id),
    );

    if (pick === -1) {
      const zn = String(zone.name ?? "").trim();
      pick = persisted.findIndex(
        (candidate, index) =>
          !usedIndices.has(index) &&
          zn !== "" &&
          String(readAttr(candidate, "name") ?? "").trim() === zn,
      );
    }

    if (pick === -1) {
      pick = persisted.findIndex((_, index) => !usedIndices.has(index));
    }

    if (pick === -1) return null;
    usedIndices.add(pick);
    return persisted[pick];
  };

  return zones.map((zone) => {
    const p = takePersistedZone(zone);
    if (!p) return zone;

    const pid = readAttr(p, "id");
    return {
      ...zone,
      id: zone.id ?? pid,
    };
  });
};

/** After PATCH — attach persisted event_requirement row ids (by id or display name). */
export const mergePersistedPortalRequirementsIntoFormValues = (
  rows = [],
  attrs,
) => {
  const persisted =
    attrs.event_requirements ?? attrs.event_requirements_attributes ?? [];
  if (!Array.isArray(persisted) || !persisted.length) return rows;

  const usedIndices = new Set();

  const takePersisted = (row) => {
    let pick = persisted.findIndex(
      (candidate, idx) =>
        !usedIndices.has(idx) &&
        row.id != null &&
        String(readAttr(candidate, "id")) === String(row.id),
    );

    if (pick === -1) {
      const nm = String(row.name ?? "").trim().toLowerCase();
      if (nm !== "") {
        pick = persisted.findIndex(
          (candidate, idx) =>
            !usedIndices.has(idx) &&
            portalRequirementDisplayName(candidate).toLowerCase() === nm,
        );
      }
    }

    if (pick === -1) {
      pick = persisted.findIndex((_, idx) => !usedIndices.has(idx));
    }

    if (pick === -1) return null;
    usedIndices.add(pick);
    return persisted[pick];
  };

  return rows.map((row) => {
    const p = takePersisted(row);
    if (!p) return row;
    const pid = readAttr(p, "id");
    return {
      ...row,
      id: row.id ?? pid,
    };
  });
};

/** Step 1 (basic info) → JSON body fragment for POST / PATCH. */
export const buildPortalBasicInfoPayload = (values) => {
  const recurrence = values?.payment_period === "daily" ? "daily" : "monthly";
  const numberOfDaysRaw = values?.number_of_days;
  const paidDaysOffRaw = values?.paid_days_off;

  const body = {
    name: String(values?.name || "").trim(),
    description: String(values?.description || "").trim(),
    payment_period: recurrence,
    attendance_verification_method:
      values?.attendance_verification_method || "basic_verification",
    is_single_day: Boolean(values?.is_single_day),
    building_start_at: toPortalDateTime(values?.building_start_at, true),
    building_end_at: toPortalDateTime(values?.building_end_at, true),
    event_start_at: toPortalDateTime(values?.event_start_at),
    event_end_at: toPortalDateTime(values?.event_end_at),
    dismantling_start_at: toPortalDateTime(values?.dismantling_start_at, true),
    dismantling_end_at: toPortalDateTime(values?.dismantling_end_at, true),
    address:
      String(values?.location?.address || values?.address || "").trim() ||
      undefined,
    lat: values?.location?.lat === "" || values?.location?.lat == null
      ? undefined
      : Number(values.location.lat),
    lng: values?.location?.lng === "" || values?.location?.lng == null
      ? undefined
      : Number(values.location.lng),
    event_radius:
      values?.location?.radius === "" || values?.location?.radius == null
        ? undefined
        : Number(values.location.radius),
  };

  /** Monthly-only fields: omit entirely for `daily` (Company Portal contract / cleaner PATCH). */
  if (recurrence === "monthly") {
    let number_of_days =
      numberOfDaysRaw !== "" && numberOfDaysRaw != null
        ? Number(numberOfDaysRaw)
        : null;
    let paid_days_off =
      paidDaysOffRaw !== "" && paidDaysOffRaw != null
        ? Number(paidDaysOffRaw)
        : null;
    if (typeof number_of_days === "number" && !Number.isFinite(number_of_days)) {
      number_of_days = null;
    }
    if (typeof paid_days_off === "number" && !Number.isFinite(paid_days_off)) {
      paid_days_off = null;
    }
    body.number_of_days = number_of_days;
    body.paid_days_off = paid_days_off;
  }

  if (typeof body.lat === "number" && !Number.isFinite(body.lat)) delete body.lat;
  if (typeof body.lng === "number" && !Number.isFinite(body.lng)) delete body.lng;
  if (typeof body.event_radius === "number" && !Number.isFinite(body.event_radius)) {
    delete body.event_radius;
  }

  const eventTypeIdRaw = values?.event_type_id;
  if (eventTypeIdRaw !== "" && eventTypeIdRaw != null) {
    const idNum = Number(eventTypeIdRaw);
    body.event_type_id = Number.isFinite(idNum) ? idNum : eventTypeIdRaw;
  }

  const cityIdRaw = values?.location?.city_id;
  if (cityIdRaw !== "" && cityIdRaw != null) {
    const cityIdNum = Number(cityIdRaw);
    body.city_id = Number.isFinite(cityIdNum) ? cityIdNum : cityIdRaw;
  }

  const companyUserEventsAttributes = buildCompanyUserEventsAttributes(
    values?.projectManagers,
    values?.destroyedProjectManagers,
  );
  if (companyUserEventsAttributes) {
    body.company_user_events_attributes = companyUserEventsAttributes;
  }

  const clientIdRaw = values?.client?.admin_id;
  if (clientIdRaw !== "" && clientIdRaw != null) {
    const clientIdNum = Number(clientIdRaw);
    body.client_id = Number.isFinite(clientIdNum) ? clientIdNum : clientIdRaw;
  } else {
    body.client_id = null;
  }

  return body;
};

/**
 * Resolve event image: upload when `image_url` holds a File (Admin StepOne shape),
 * or reuse existing HTTPS string.
 */
export async function resolvePortalEventLogoUrl(values, options = {}) {
  const v = values?.image_url;
  if (v instanceof File || v instanceof Blob) {
    const imgExt =
      v instanceof File && typeof v.name === "string" && v.name.includes(".")
        ? v.name.split(".").pop()
        : "png";
    const imgName = `event_image.${imgExt}`;
    const data = await uploadToS3(v, imgName, directories.events, {
      onProgress: options.onProgress,
    });
    return data?.link;
  }
  const preview = typeof v === "string" ? v.trim() : "";
  if (/^https?:\/\//i.test(preview)) return preview;
  return undefined;
}

/** True when the user cleared the logo or never set one (not a File / HTTPS URL). */
export const isPortalEventLogoEmpty = (value) => {
  if (value instanceof File || value instanceof Blob) return false;
  if (typeof value === "string" && /^https?:\/\//i.test(value.trim())) {
    return false;
  }
  return true;
};

/** Async Step 1 body — sets `image_url` after S3 upload when the user picked a file. */
export async function buildPortalBasicInfoRequestBody(values, options = {}) {
  const body = buildPortalBasicInfoPayload(values);
  const imageUrl = await resolvePortalEventLogoUrl(values, {
    onProgress: options.onUploadProgress,
  });
  if (imageUrl) {
    body.image_url = imageUrl;
  } else if (isPortalEventLogoEmpty(values?.image_url)) {
    body.image_url = null;
  }
  return body;
}

/** Zone layout map: upload new file to S3 or reuse existing HTTPS URL (Admin Step 3 parity). */
export async function resolvePortalZonesImageUrl(values, options = {}) {
  const v = values?.zones_image_url;
  if (v instanceof File || v instanceof Blob) {
    let ext = "png";
    if (v instanceof File && typeof v.name === "string" && v.name.includes(".")) {
      ext = v.name.split(".").pop() || "png";
    } else if (v.type === "application/pdf") {
      ext = "pdf";
    }
    const fileName = `zone_map.${ext}`;
    const data = await uploadToS3(v, fileName, directories.events, {
      onProgress: options.onProgress,
    });
    return data?.link;
  }
  const s = typeof v === "string" ? v.trim() : "";
  if (/^https?:\/\//i.test(s)) return s;
  return undefined;
}

const marginOrOmit = (v) => {
  if (v === "" || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const omitEmptyTrainingFields = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => {
      if (v === undefined || v === null) return false;
      if (typeof v === "number" && !Number.isFinite(v)) return false;
      if (typeof v === "string" && v.trim() === "") return false;
      return true;
    }),
  );

const cleanTraining = (training) => {
  if (!training || typeof training !== "object") return undefined;
  if (training._destroy === true && training.id != null && training.id !== "") {
    return { id: training.id, _destroy: true };
  }
  const t = {
    name: (() => {
      const s = String(training.name || "").trim();
      return s || undefined;
    })(),
    description: (() => {
      const s = String(training.description || "").trim();
      return s || undefined;
    })(),
    payroll_amount:
      training.payroll_amount === "" || training.payroll_amount == null
        ? undefined
        : Number(training.payroll_amount),
    number_of_days:
      training.number_of_days === "" || training.number_of_days == null
        ? undefined
        : Number(training.number_of_days),
    starts_at: toPortalDateTime(training.starts_at),
    ends_at: toPortalDateTime(training.ends_at),
    lat: training.lat === "" || training.lat == null ? undefined : Number(training.lat),
    lng: training.lng === "" || training.lng == null ? undefined : Number(training.lng),
    location_radius:
      training.location_radius === "" || training.location_radius == null
        ? undefined
        : Number(training.location_radius),
    location_address: String(training.location_address || "").trim() || undefined,
    before_check_in_margin: marginOrOmit(training.before_check_in_margin),
    after_check_in_margin: marginOrOmit(training.after_check_in_margin),
    before_check_out_margin: marginOrOmit(training.before_check_out_margin),
    after_check_out_margin: marginOrOmit(training.after_check_out_margin),
  };
  if (training.id) t.id = training.id;

  const pruned = omitEmptyTrainingFields(t);
  if (Object.keys(pruned).length === 0) return undefined;
  return pruned;
};

const createCleanShift = (eventBounds) => {
  const baseDay = eventBounds?.eventStartAt;
  return (shift) => {
    let startsAt = shiftClockToIsoForPayload(shift?.starts_at, baseDay);
    let endsAt = shiftClockToIsoForPayload(shift?.ends_at, baseDay);
    endsAt = adjustOvernightShiftEnd(startsAt, endsAt);
    const trainingAttributes = cleanTraining(shift?.training_attributes);
    const row = {
      shift_type: shift?.shift_type || "full_time",
      starts_at: startsAt,
      ends_at: endsAt,
      payroll_amount:
        shift?.payroll_amount === "" || shift?.payroll_amount == null
          ? undefined
          : Number(shift.payroll_amount),
      before_check_in_margin: marginOrOmit(shift?.before_check_in_margin),
      after_check_in_margin: marginOrOmit(shift?.after_check_in_margin),
      before_check_out_margin: marginOrOmit(shift?.before_check_out_margin),
      after_check_out_margin: marginOrOmit(shift?.after_check_out_margin),
      training_attributes: trainingAttributes,
    };
    if (shift?.id) row.id = shift.id;
    if (shift?._destroy) row._destroy = true;
    return row;
  };
};

/** Step 2 → event_roles_attributes fragment. */
export const buildPortalRolesPayload = (
  values,
  eventBounds = {},
  _options = {},
) => {
  void _options;
  const cleanShift = createCleanShift(eventBounds);
  const roles = values?.event_roles_attributes || [];
  const event_roles_attributes = roles.map((role) => {
    const row = {
      role_id:
        role?.role_id === "" || role?.role_id == null
          ? undefined
          : Number(role.role_id),
      uniform_code: String(role?.uniform_code || "").trim() || undefined,
      maximum_number_of_males:
        role?.maximum_number_of_males === "" || role?.maximum_number_of_males == null
          ? undefined
          : Number(role.maximum_number_of_males),
      maximum_number_of_females:
        role?.maximum_number_of_females === "" ||
        role?.maximum_number_of_females == null
          ? undefined
          : Number(role.maximum_number_of_females),
      event_role_shifts_attributes: (role?.event_role_shifts_attributes || []).map(
        cleanShift,
      ),
    };
    if (role?.id) row.id = role.id;
    if (role?._destroy) row._destroy = true;
    return row;
  });
  return { event_roles_attributes };
};

const trimPortalStringOrUndef = (v) => {
  const s = String(v ?? "").trim();
  return s === "" ? undefined : s;
};

/**
 * Step 3 — `zones_attributes` (same Rails shape as Admin StepThree manager rules).
 */
export const buildPortalZonesPayload = (values) => ({
  zones_attributes: (values?.zones_attributes || []).map((z) => {
    const row = {
      name: String(z?.name || "").trim(),
      image_url: trimPortalStringOrUndef(z?.image_url),
    };

    const sidRaw = z?.supervisor_id;
    const sidNum =
      sidRaw !== "" && sidRaw != null && sidRaw !== undefined
        ? Number(sidRaw)
        : NaN;
    if (Number.isFinite(sidNum)) {
      row.supervisor_id = sidNum;
    } else if (z?.id) {
      row.supervisor_id = null;
    }

    Object.keys(row).forEach((k) => {
      if (row[k] === undefined) delete row[k];
    });

    if (z?.id) row.id = z.id;
    if (z?._destroy) row._destroy = true;

    return row;
  }),
});

const normalizePortalRequirementCatalog = (catalog = []) =>
  (Array.isArray(catalog) ? catalog : []).map((item) => ({
    id:
      item?.id !== "" && item?.id != null && Number.isFinite(Number(item.id))
        ? Number(item.id)
        : undefined,
    name: String(item?.name ?? "").trim(),
  }));

/** Resolve Rails `requirement_id` from row + portal lookup catalog (matches §2.5 PROLINE-2274). */
export const resolvePortalRequirementRowId = (row, requirementCatalog = []) => {
  const normalized = normalizePortalRequirementCatalog(requirementCatalog);
  const raw = row?.requirement_id;
  if (raw !== "" && raw != null && raw !== undefined) {
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  const nm = String(row?.name ?? "").trim().toLowerCase();
  if (!nm) return undefined;
  const hit = normalized.find((c) => c.name.toLowerCase() === nm);
  return hit?.id;
};

export const buildPortalRequirementsPayload = (
  values,
  requirementCatalog = [],
) => ({
  event_requirements_attributes: (values?.logistics_requirements_attributes || [])
    .map((row) => {
      const requirement_id = resolvePortalRequirementRowId(row, requirementCatalog);
      const payloadRow = {
        requirement_id,
        quantity:
          row?.quantity === "" || row?.quantity == null
            ? undefined
            : String(row.quantity),
        explanation: trimPortalStringOrUndef(row?.notes),
      };
      if (row?.id) payloadRow.id = row.id;
      if (row?._destroy) payloadRow._destroy = true;

      Object.keys(payloadRow).forEach((key) => {
        if (payloadRow[key] === undefined) delete payloadRow[key];
      });

      return { row, requirement_id, payloadRow };
    })
    .filter(
      ({ row, requirement_id }) =>
        Boolean(row?._destroy) ||
        (row?.id != null && row?.id !== "") ||
        (requirement_id != null &&
          requirement_id !== "" &&
          Number.isFinite(Number(requirement_id))),
    )
    .map(({ payloadRow }) => payloadRow),
});

export const buildPortalStepThreePayload = (values, options = {}) => {
  const zi = values?.zones_image_url;
  const zones_image_url =
    typeof zi === "string" && zi.trim() !== ""
      ? zi.trim()
      : null;

  return {
    ...buildPortalZonesPayload(values),
    ...buildPortalRequirementsPayload(
      values,
      options.requirementCatalog ?? [],
    ),
    zones_image_url,
  };
};

